import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  RefreshCw,
  TrendingUp,
  ChevronLeft,
  ChevronRight,
  Filter,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';
import { AppShell } from '@/components/layout/AppShell';
import { Header } from '@/components/layout/Header';
import { Avatar } from '@/components/ui/Avatar';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Spinner';
import { useToast } from '@/components/ui/Toast';
import { cn } from '@/lib/cn';

/* ==========================================================================
   AdminMarketing — Segmentazione clienti per marketing.

   Tabella con dati aggregati dai clienti, filtri per tipo pelle,
   cellulite e giorni di inattivita. Refresh materialized view.
   ========================================================================== */

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface MarketingClient {
  clientId: number;
  nome: string;
  cognome: string;
  email: string | null;
  telefono: string | null;
  tipoPelle: string | null;
  celluliteGrado: number | null;
  ultimaVisita: string | null;
  giorniInattivita: number | null;
  ultimaSedutaLaser: string | null;
  sedutelaserTotali: number;
}

interface MarketingResponse {
  data: MarketingClient[];
  total: number;
}

interface Filters {
  tipoPelle: string;
  celluliteGrado: string;
  giorniInattivitaMin: string;
  giorniInattivitaMax: string;
}

const EMPTY_FILTERS: Filters = {
  tipoPelle: '',
  celluliteGrado: '',
  giorniInattivitaMin: '',
  giorniInattivitaMax: '',
};

const PAGE_SIZE = 50;

const TIPO_PELLE_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: 'normale', label: 'Normale' },
  { value: 'secca', label: 'Secca' },
  { value: 'grassa', label: 'Grassa' },
  { value: 'mista', label: 'Mista' },
  { value: 'sensibile', label: 'Sensibile' },
  { value: 'acneica', label: 'Acneica' },
];

const CELLULITE_OPTIONS = [
  { value: '', label: 'Tutti' },
  { value: '0', label: 'Grado 0' },
  { value: '1', label: 'Grado 1' },
  { value: '2', label: 'Grado 2' },
  { value: '3', label: 'Grado 3' },
  { value: '4', label: 'Grado 4' },
];

/* --------------------------------------------------------------------------
   Helpers
   -------------------------------------------------------------------------- */

function formatDate(dateStr: string | null | undefined): string {
  if (!dateStr) return '\u2014';
  try {
    return new Intl.DateTimeFormat('it-IT', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
    }).format(new Date(dateStr));
  } catch {
    return '\u2014';
  }
}

function getInactivityColor(days: number | null): string {
  if (days === null) return 'text-text-muted';
  if (days < 30) return 'text-status-positive font-semibold';
  if (days < 60) return 'text-amber-500 font-semibold';
  return 'text-status-negative font-semibold';
}

function getInactivityBg(days: number | null): string {
  if (days === null) return '';
  if (days < 30) return 'bg-status-positive-light';
  if (days < 60) return 'bg-amber-50';
  return 'bg-status-negative-light';
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function AdminMarketing() {
  const toast = useToast();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const isSuperAdmin = currentUser?.role === 'super_admin';

  // ------------------------------------------------------------------
  // State
  // ------------------------------------------------------------------

  const [clients, setClients] = useState<MarketingClient[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [pendingFilters, setPendingFilters] = useState<Filters>(EMPTY_FILTERS);
  const [showFilters, setShowFilters] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefreshedAt, setLastRefreshedAt] = useState<string | null>(null);

  // ------------------------------------------------------------------
  // Build query string
  // ------------------------------------------------------------------

  const buildQueryString = useCallback(
    (currentFilters: Filters, currentPage: number): string => {
      const params = new URLSearchParams();
      if (currentFilters.tipoPelle)
        params.set('tipo_pelle', currentFilters.tipoPelle);
      if (currentFilters.celluliteGrado)
        params.set('cellulite_grado', currentFilters.celluliteGrado);
      if (currentFilters.giorniInattivitaMin)
        params.set(
          'giorni_inattivita_min',
          currentFilters.giorniInattivitaMin,
        );
      if (currentFilters.giorniInattivitaMax)
        params.set(
          'giorni_inattivita_max',
          currentFilters.giorniInattivitaMax,
        );
      params.set('page', String(currentPage));
      params.set('limit', String(PAGE_SIZE));
      return params.toString();
    },
    [],
  );

  // ------------------------------------------------------------------
  // Load data
  // ------------------------------------------------------------------

  const loadData = useCallback(
    async (currentFilters: Filters, currentPage: number) => {
      setIsLoading(true);
      setError(null);
      try {
        const qs = buildQueryString(currentFilters, currentPage);
        const data = await api.get<MarketingResponse>(
          `/admin/marketing?${qs}`,
        );
        setClients(data?.data ?? []);
        setTotal(data?.total ?? 0);
      } catch (err) {
        if (err instanceof ApiError) {
          setError(err.message);
        } else {
          setError('Errore di connessione. Riprova.');
        }
      } finally {
        setIsLoading(false);
      }
    },
    [buildQueryString],
  );

  useEffect(() => {
    loadData(filters, page);
  }, [loadData, filters, page]);

  // ------------------------------------------------------------------
  // Apply filters
  // ------------------------------------------------------------------

  const applyFilters = useCallback(() => {
    setFilters(pendingFilters);
    setPage(1);
    setShowFilters(false);
  }, [pendingFilters]);

  const resetFilters = useCallback(() => {
    setPendingFilters(EMPTY_FILTERS);
    setFilters(EMPTY_FILTERS);
    setPage(1);
    setShowFilters(false);
  }, []);

  const activeFilterCount = useMemo(() => {
    return Object.values(filters).filter(Boolean).length;
  }, [filters]);

  // ------------------------------------------------------------------
  // Refresh materialized view (super_admin only)
  // ------------------------------------------------------------------

  const handleRefresh = useCallback(async () => {
    setIsRefreshing(true);
    try {
      const result = await api.post<{ success: boolean; refreshedAt: string }>(
        '/admin/marketing/refresh',
      );
      setLastRefreshedAt(result?.refreshedAt ?? new Date().toISOString());
      toast.success('Dati aggiornati con successo');
      // Reload data after refresh
      await loadData(filters, page);
    } catch (err) {
      if (err instanceof ApiError) {
        toast.error(err.message || 'Errore durante il refresh');
      } else {
        toast.error('Errore di connessione. Riprova.');
      }
    } finally {
      setIsRefreshing(false);
    }
  }, [filters, page, loadData, toast]);

  // ------------------------------------------------------------------
  // Pagination
  // ------------------------------------------------------------------

  const totalPages = Math.ceil(total / PAGE_SIZE);

  // ------------------------------------------------------------------
  // Loading skeleton
  // ------------------------------------------------------------------

  if (isLoading && clients.length === 0) {
    return (
      <AppShell header={<Header showBack title="Marketing" />}>
        <div className="space-y-3">
          {[1, 2, 3, 4, 5].map((i) => (
            <MarketingSkeleton key={i} />
          ))}
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Error state
  // ------------------------------------------------------------------

  if (error && clients.length === 0) {
    return (
      <AppShell header={<Header showBack title="Marketing" />}>
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div
            className={cn(
              'mb-5 flex h-16 w-16 items-center justify-center',
              'rounded-full bg-status-negative-light',
            )}
          >
            <TrendingUp
              className="h-7 w-7 text-status-negative"
              strokeWidth={1.5}
            />
          </div>
          <p className="text-lg font-[family-name:var(--font-heading)] font-semibold text-text-primary">
            {error}
          </p>
          <Button
            variant="secondary"
            size="md"
            className="mt-6"
            leftIcon={<RefreshCw strokeWidth={2} />}
            onClick={() => loadData(filters, page)}
          >
            Riprova
          </Button>
        </div>
      </AppShell>
    );
  }

  // ------------------------------------------------------------------
  // Main render
  // ------------------------------------------------------------------

  return (
    <AppShell
      header={
        <Header
          showBack
          title="Marketing"
          actions={
            <div className="flex items-center gap-2">
              <button
                onClick={() => {
                  setPendingFilters(filters);
                  setShowFilters((prev) => !prev);
                }}
                className={cn(
                  'relative flex items-center justify-center gap-1.5',
                  'h-9 px-3 rounded-[var(--radius-sm)]',
                  'text-sm font-[family-name:var(--font-heading)] font-semibold',
                  'transition-colors duration-[var(--duration-fast)]',
                  'cursor-pointer',
                  showFilters || activeFilterCount > 0
                    ? 'bg-accent-primary text-text-inverted'
                    : 'bg-surface-card border border-border-default text-text-secondary hover:border-border-hover',
                )}
              >
                <Filter className="h-4 w-4" strokeWidth={2} />
                Filtri
                {activeFilterCount > 0 && (
                  <span className="ml-1 flex items-center justify-center h-4 w-4 rounded-full bg-white/30 text-[10px] font-bold">
                    {activeFilterCount}
                  </span>
                )}
              </button>
              {isSuperAdmin && (
                <Button
                  size="sm"
                  variant="secondary"
                  leftIcon={<RefreshCw strokeWidth={2} />}
                  onClick={handleRefresh}
                  loading={isRefreshing}
                >
                  Aggiorna
                </Button>
              )}
            </div>
          }
        />
      }
    >
      <div className="space-y-5 pb-8">
        {/* ---- Filter panel ---- */}
        {showFilters && (
          <FilterPanel
            filters={pendingFilters}
            onChange={setPendingFilters}
            onApply={applyFilters}
            onReset={resetFilters}
          />
        )}

        {/* ---- Stats bar ---- */}
        <div className="flex items-center justify-between text-sm text-text-muted">
          <span>
            <strong className="text-text-primary">{total}</strong> clienti
            {activeFilterCount > 0 && ' (filtrati)'}
          </span>
          {lastRefreshedAt && (
            <span className="text-xs">
              Dati al{' '}
              {new Intl.DateTimeFormat('it-IT', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              }).format(new Date(lastRefreshedAt))}
            </span>
          )}
        </div>

        {/* ---- Legend ---- */}
        <div className="flex items-center gap-4 text-xs text-text-muted">
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-status-positive" />
            Attivo (&lt;30g)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-amber-400" />
            A rischio (30-60g)
          </span>
          <span className="flex items-center gap-1.5">
            <span className="inline-block h-2.5 w-2.5 rounded-full bg-status-negative" />
            Inattivo (&gt;60g)
          </span>
        </div>

        {/* ---- Client cards ---- */}
        {isLoading ? (
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <MarketingSkeleton key={i} />
            ))}
          </div>
        ) : clients.length === 0 ? (
          <div className="flex flex-col items-center py-12 text-center">
            <div
              className={cn(
                'mb-4 flex h-14 w-14 items-center justify-center',
                'rounded-full bg-surface-sunken',
              )}
            >
              <TrendingUp
                className="h-6 w-6 text-text-muted"
                strokeWidth={1.5}
              />
            </div>
            <p className="text-sm text-text-muted">
              Nessun cliente trovato con i filtri selezionati
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {clients.map((client) => (
              <MarketingClientCard
                key={client.clientId}
                client={client}
                onClick={() => navigate(`/clients/${client.clientId}`)}
              />
            ))}
          </div>
        )}

        {/* ---- Pagination ---- */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              size="sm"
              leftIcon={<ChevronLeft strokeWidth={2} />}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Precedente
            </Button>
            <span className="text-sm text-text-muted">
              Pagina {page} di {totalPages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              rightIcon={<ChevronRight strokeWidth={2} />}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Successiva
            </Button>
          </div>
        )}
      </div>
    </AppShell>
  );
}

/* ==========================================================================
   Sub-components
   ========================================================================== */

/* --------------------------------------------------------------------------
   Filter Panel
   -------------------------------------------------------------------------- */

function FilterPanel({
  filters,
  onChange,
  onApply,
  onReset,
}: {
  filters: Filters;
  onChange: (f: Filters) => void;
  onApply: () => void;
  onReset: () => void;
}) {
  const selectClass = cn(
    'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
    'bg-surface-card border border-border-default',
    'px-4 text-base text-text-primary',
    'font-[family-name:var(--font-body)]',
    'transition-all duration-[var(--duration-normal)] ease-[var(--ease-out)]',
    'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
    'focus:outline-none',
    'hover:border-border-hover',
  );

  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-border-default',
        'bg-surface-card p-4 space-y-4',
        'shadow-xs',
      )}
    >
      <h4 className="font-[family-name:var(--font-heading)] text-sm font-bold text-text-primary">
        Filtri
      </h4>

      <div className="grid grid-cols-2 gap-3">
        {/* Tipo pelle */}
        <div className="flex flex-col gap-1.5">
          <label className="font-[family-name:var(--font-heading)] text-xs font-semibold text-text-secondary">
            Tipo pelle
          </label>
          <select
            value={filters.tipoPelle}
            onChange={(e) =>
              onChange({ ...filters, tipoPelle: e.target.value })
            }
            className={selectClass}
          >
            {TIPO_PELLE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Cellulite */}
        <div className="flex flex-col gap-1.5">
          <label className="font-[family-name:var(--font-heading)] text-xs font-semibold text-text-secondary">
            Cellulite
          </label>
          <select
            value={filters.celluliteGrado}
            onChange={(e) =>
              onChange({ ...filters, celluliteGrado: e.target.value })
            }
            className={selectClass}
          >
            {CELLULITE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {/* Giorni inattivita min */}
        <div className="flex flex-col gap-1.5">
          <label className="font-[family-name:var(--font-heading)] text-xs font-semibold text-text-secondary">
            Inattivita (min giorni)
          </label>
          <input
            type="number"
            min={0}
            placeholder="Es. 30"
            value={filters.giorniInattivitaMin}
            onChange={(e) =>
              onChange({ ...filters, giorniInattivitaMin: e.target.value })
            }
            className={cn(
              'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
              'bg-surface-card border border-border-default',
              'px-4 text-base text-text-primary',
              'font-[family-name:var(--font-body)]',
              'placeholder:text-text-muted',
              'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
              'focus:outline-none hover:border-border-hover',
            )}
          />
        </div>

        {/* Giorni inattivita max */}
        <div className="flex flex-col gap-1.5">
          <label className="font-[family-name:var(--font-heading)] text-xs font-semibold text-text-secondary">
            Inattivita (max giorni)
          </label>
          <input
            type="number"
            min={0}
            placeholder="Es. 90"
            value={filters.giorniInattivitaMax}
            onChange={(e) =>
              onChange({ ...filters, giorniInattivitaMax: e.target.value })
            }
            className={cn(
              'h-[var(--touch-min)] w-full rounded-[var(--radius-md)]',
              'bg-surface-card border border-border-default',
              'px-4 text-base text-text-primary',
              'font-[family-name:var(--font-body)]',
              'placeholder:text-text-muted',
              'focus:border-border-focus focus:ring-2 focus:ring-accent-primary-light',
              'focus:outline-none hover:border-border-hover',
            )}
          />
        </div>
      </div>

      <div className="flex gap-3">
        <Button variant="ghost" size="md" className="flex-1" onClick={onReset}>
          Reset
        </Button>
        <Button size="md" className="flex-1" onClick={onApply}>
          Applica
        </Button>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Marketing Client Card
   -------------------------------------------------------------------------- */

function MarketingClientCard({
  client,
  onClick,
}: {
  client: MarketingClient;
  onClick: () => void;
}) {
  const fullName = `${client.nome} ${client.cognome}`;
  const inactivityColor = getInactivityColor(client.giorniInattivita);
  const inactivityBg = getInactivityBg(client.giorniInattivita);

  return (
    <div
      className={cn(
        'rounded-[var(--radius-lg)] border border-border-default bg-surface-card',
        'px-5 py-4',
        'transition-all duration-[var(--duration-fast)]',
        'hover:border-border-hover hover:shadow-sm',
        'cursor-pointer',
      )}
      onClick={onClick}
      role="button"
    >
      {/* Top row */}
      <div className="flex items-center gap-3 mb-3">
        <Avatar name={fullName} size="md" />
        <div className="flex-1 min-w-0">
          <p className="font-[family-name:var(--font-heading)] text-sm font-semibold text-text-primary truncate">
            {fullName}
          </p>
          <p className="text-xs text-text-muted truncate">
            {client.email ?? client.telefono ?? 'Nessun contatto'}
          </p>
        </div>

        {/* Inactivity badge */}
        {client.giorniInattivita !== null && (
          <div
            className={cn(
              'shrink-0 px-3 py-1 rounded-[var(--radius-full)]',
              inactivityBg,
            )}
          >
            <span className={cn('text-xs', inactivityColor)}>
              {client.giorniInattivita}g
            </span>
          </div>
        )}
      </div>

      {/* Data grid */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs">
        <DataField label="Tipo pelle" value={client.tipoPelle} />
        <DataField
          label="Cellulite"
          value={
            client.celluliteGrado !== null
              ? `Grado ${client.celluliteGrado}`
              : null
          }
        />
        <DataField
          label="Ultima visita"
          value={formatDate(client.ultimaVisita)}
        />
        <DataField
          label="Ultima seduta laser"
          value={formatDate(client.ultimaSedutaLaser)}
        />
        <DataField
          label="Sedute laser totali"
          value={
            client.sedutelaserTotali > 0
              ? String(client.sedutelaserTotali)
              : '0'
          }
        />
      </div>
    </div>
  );
}

function DataField({
  label,
  value,
}: {
  label: string;
  value: string | null | undefined;
}) {
  return (
    <div>
      <span className="text-text-muted">{label}: </span>
      <span className="text-text-secondary font-medium">
        {value ?? '\u2014'}
      </span>
    </div>
  );
}

/* --------------------------------------------------------------------------
   Skeleton
   -------------------------------------------------------------------------- */

function MarketingSkeleton() {
  return (
    <div className="rounded-[var(--radius-lg)] border border-border-default bg-surface-card px-5 py-4 space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton height={40} className="w-10 rounded-full shrink-0" />
        <div className="flex-1 space-y-1.5">
          <Skeleton height={14} className="w-2/5" />
          <Skeleton height={12} className="w-3/5" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-2">
        <Skeleton height={12} className="w-full" />
        <Skeleton height={12} className="w-full" />
        <Skeleton height={12} className="w-full" />
        <Skeleton height={12} className="w-full" />
      </div>
    </div>
  );
}
